import React, { useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Popover,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AccessTime,
  ChatBubbleOutline,
  Clear,
  Close,
  ContactPhone,
  // Logout as LogoutIcon,
  NotificationsNone,
  Person,
  Phone,
  Pin,
  Refresh,
  // Print,
  Search,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import LoginPage from '../Members/LoginPage';
import {
  checkOutChild,
  useAdminUnreadMessages,
  useAllActiveCheckins,
} from './services';
import { CheckInRecord } from './types';
import WalkInDialog from './WalkInDialog';
import { printLabels } from './printLabels';
import ChatWindow from './ChatWindow';

const getAge = (dateOfBirth: Date | null): string => {
  if (!dateOfBirth) return 'N/A';
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }
  return age.toString();
};

const getAgeNum = (dateOfBirth: Date | null): number | null => {
  if (!dateOfBirth) return null;
  const today = new Date();
  let age = today.getFullYear() - dateOfBirth.getFullYear();
  const m = today.getMonth() - dateOfBirth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dateOfBirth.getDate())) age--;
  return age;
};

const AGE_MIN = 0;
const AGE_MAX = 18;

interface MsgConvo {
  childId: string;
  familyId: string;
  childName: string;
}

const CheckInCard: React.FC<{
  checkin: CheckInRecord;
  onClick: () => void;
  onPrint: () => void;
  onCheckOut: () => void;
  onMessage: () => void;
  unreadCount: number;
}> = ({ checkin, onClick, onPrint, onCheckOut, onMessage, unreadCount }) => (
  <Card
    elevation={3}
    onClick={onClick}
    sx={{
      borderRadius: 3,
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'box-shadow 0.2s, transform 0.1s',
      '&:hover': { boxShadow: 8, transform: 'translateY(-2px)' },
      '&:active': { transform: 'scale(0.98)' },
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      ...(checkin.isWalkIn && {
        border: '2px solid',
        borderColor: 'warning.main',
      }),
    }}
  >
    <Box sx={{ position: 'relative' }}>
      {checkin.photoUrl ? (
        <CardMedia
          component="img"
          image={checkin.photoUrl}
          alt={checkin.childFirstName}
          sx={{ height: 200, objectFit: 'cover', objectPosition: 'center' }}
        />
      ) : (
        <Box
          sx={{
            height: 200,
            bgcolor: checkin.isWalkIn ? 'warning.main' : 'primary.main',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: 72,
              color: 'white',
              fontWeight: 'bold',
              lineHeight: 1,
            }}
          >
            {checkin.childFirstName?.[0]}
          </Typography>
        </Box>
      )}
      {checkin.isWalkIn && (
        <Chip
          label="Walk-In"
          color="warning"
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            fontWeight: 700,
            boxShadow: 2,
          }}
        />
      )}
      {!checkin.isWalkIn && (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onMessage();
          }}
          sx={{
            position: 'absolute',
            top: 6,
            left: 6,
            bgcolor: 'rgba(0,0,0,0.45)',
            color: 'white',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
          }}
        >
          <Badge badgeContent={unreadCount} color="error">
            <ChatBubbleOutline fontSize="small" />
          </Badge>
        </IconButton>
      )}
      {checkin.allergies && checkin.allergies !== 'N/A' && (
        <Chip
          icon={<Warning fontSize="small" />}
          label={checkin.allergies}
          color="error"
          size="small"
          sx={{
            position: 'absolute',
            bottom: 8,
            left: 8,
            fontWeight: 700,
            boxShadow: 2,
            maxWidth: '85%',
          }}
        />
      )}
      {checkin.dateOfBirth && (
        <Chip
          label={`${getAge(checkin.dateOfBirth)} yrs`}
          size="small"
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            fontWeight: 700,
            boxShadow: 2,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: 'white',
          }}
        />
      )}
    </Box>

    <CardContent sx={{ pt: 0.5, pb: '12px !important', flex: 1 }}>
      <Typography variant="h6" fontWeight="bold" textAlign="center" noWrap>
        {checkin.childFirstName} {checkin.childLastName}{' '}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} mt={0}>
        <AccessTime sx={{ fontSize: 15, color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary">
          {checkin.checkInTime.toLocaleString(undefined, {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </Typography>
      </Stack>
      {/* {checkin.parentName && (
        <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
          <Person sx={{ fontSize: 15, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary" noWrap>
            {checkin.parentName}
          </Typography>
        </Stack>
      )} */}
      {checkin.pickupPin && (
        <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
          <Pin sx={{ fontSize: 15, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            PIN: {checkin.pickupPin}
          </Typography>
        </Stack>
      )}
    </CardContent>

    <CardActions sx={{ px: 1, pb: 2, pt: 0, gap: 0 }}>
      <Button
        fullWidth
        variant="outlined"
        size="small"
        sx={{ borderRadius: 2 }}
        onClick={(e) => {
          e.stopPropagation();
          onPrint();
        }}
      >
        Print Tag
      </Button>
      <Button
        fullWidth
        variant="outlined"
        color="error"
        size="small"
        sx={{ borderRadius: 2 }}
        onClick={(e) => {
          e.stopPropagation();
          onCheckOut();
        }}
      >
        Checkout
      </Button>
    </CardActions>
  </Card>
);

const CheckInDetailDialog: React.FC<{
  checkin: CheckInRecord | null;
  onClose: () => void;
  onCheckOut: (c: CheckInRecord) => void;
  onPrint: (c: CheckInRecord) => void;
  onMessage: (c: CheckInRecord) => void;
  unreadCount: number;
}> = ({ checkin, onClose, onCheckOut, onPrint, onMessage, unreadCount }) => (
  <Dialog open={!!checkin} onClose={onClose} maxWidth="xs" fullWidth>
    {checkin && (
      <>
        <Box sx={{ position: 'relative' }}>
          {checkin.photoUrl ? (
            <CardMedia
              component="img"
              image={checkin.photoUrl}
              alt={checkin.childFirstName}
              sx={{ height: 250, objectFit: 'cover', objectPosition: 'center' }}
            />
          ) : (
            <Box
              sx={{
                height: 180,
                bgcolor: checkin.isWalkIn ? 'warning.main' : 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: 80,
                  color: 'white',
                  fontWeight: 'bold',
                  lineHeight: 1,
                }}
              >
                {checkin.childFirstName?.[0]}
              </Typography>
            </Box>
          )}
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'rgba(0,0,0,0.45)',
              color: 'white',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          {checkin.isWalkIn && (
            <Chip
              label="Walk-In"
              color="warning"
              size="small"
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                fontWeight: 700,
                boxShadow: 2,
              }}
            />
          )}
        </Box>

        <DialogTitle sx={{ pb: 0 }}>
          {checkin.childFirstName} {checkin.childLastName}{' '}
          <Typography
            component="span"
            variant="h6"
            fontWeight="normal"
            color="text.secondary"
          >
            ({getAge(checkin.dateOfBirth ?? null)})
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={1.2} sx={{ mt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <AccessTime sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2">
                {checkin.checkInTime.toLocaleString(undefined, {
                  dateStyle: 'medium',
                  timeStyle: 'short',
                })}
              </Typography>
            </Stack>

            {checkin.parentName && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">{checkin.parentName}</Typography>
              </Stack>
            )}

            {checkin.parentPhone && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Phone sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Link href={`tel:${checkin.parentPhone}`} variant="body2">
                  {checkin.parentPhone}
                </Link>
              </Stack>
            )}

            {checkin.pickupPin && (
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Pin sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="body2">
                  Pickup PIN: <strong>{checkin.pickupPin}</strong>
                </Typography>
              </Stack>
            )}

            {checkin.allergies && checkin.allergies !== 'N/A' && (
              <>
                <Divider />
                <Chip
                  icon={<Warning />}
                  label={checkin.allergies}
                  color="error"
                  sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
                />
              </>
            )}

            {checkin.specialNotes && (
              <Typography
                variant="body2"
                color="text.secondary"
                fontStyle="italic"
              >
                &ldquo;{checkin.specialNotes}&rdquo;
              </Typography>
            )}

            {(checkin.emergencyContacts?.length ?? 0) > 0 && (
              <>
                <Divider />
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ContactPhone sx={{ fontSize: 18, color: 'primary.main' }} />
                  <Typography variant="body2" fontWeight={600}>
                    Emergency Contacts
                  </Typography>
                </Stack>
                {(checkin.emergencyContacts ?? []).map((contact, i) => (
                  <Box key={i} sx={{ pl: 3.5 }}>
                    {i > 0 && <Divider sx={{ mb: 1 }} />}
                    <Typography variant="body2" fontWeight={600}>
                      {contact.name}
                    </Typography>
                    <Link
                      href={`tel:${contact.phone}`}
                      variant="body2"
                      display="block"
                    >
                      {contact.phone}
                    </Link>
                  </Box>
                ))}
              </>
            )}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ gap: 1, px: 2, pb: 2 }}>
          {!checkin.isWalkIn && (
            <Badge badgeContent={unreadCount} color="error">
              <Button
                variant="outlined"
                startIcon={<ChatBubbleOutline />}
                onClick={() => {
                  onMessage(checkin);
                  onClose();
                }}
              >
                Message
              </Button>
            </Badge>
          )}
          <Button
            variant="outlined"
            onClick={() => {
              onPrint(checkin);
              onClose();
            }}
          >
            Print Tag
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              onCheckOut(checkin);
              onClose();
            }}
          >
            Check Out
          </Button>
        </DialogActions>
      </>
    )}
  </Dialog>
);

const CheckInAdmin: React.FC = () => {
  const navigate = useNavigate();
  const { userData, loading: authLoading, isChildcareAdmin } = useAuth();
  const { checkins, loading } = useAllActiveCheckins(true);
  const unreadMsgs = useAdminUnreadMessages();
  const [detailTarget, setDetailTarget] = useState<CheckInRecord | null>(null);
  const [checkOutTarget, setCheckOutTarget] = useState<CheckInRecord | null>(
    null
  );
  const [msgTarget, setMsgTarget] = useState<MsgConvo | null>(null);
  const [notifAnchor, setNotifAnchor] = useState<HTMLElement | null>(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [search, setSearch] = useState('');
  const [walkInOpen, setWalkInOpen] = useState(false);
  const [ageRange, setAgeRange] = useState<[number, number]>([
    AGE_MIN,
    AGE_MAX,
  ]);

  const unreadByChild = useMemo(() => {
    const map = new Map<string, number>();
    unreadMsgs.forEach((m) =>
      map.set(m.childId, (map.get(m.childId) ?? 0) + 1)
    );
    return map;
  }, [unreadMsgs]);

  const unreadConversations = useMemo<MsgConvo[]>(() => {
    const seen = new Map<string, MsgConvo>();
    unreadMsgs.forEach((m) => {
      if (seen.has(m.childId)) return;
      const match = checkins.find((c) => c.childId === m.childId);
      seen.set(m.childId, {
        childId: m.childId,
        familyId: m.familyId,
        childName: match
          ? `${match.childFirstName} ${match.childLastName}`
          : 'Unknown Child',
      });
    });
    return Array.from(seen.values());
  }, [unreadMsgs, checkins]);

  const filteredCheckins = useMemo(() => {
    const q = search.trim().toLowerCase();
    const [minAge, maxAge] = ageRange;
    return checkins.filter((c) => {
      if (
        q &&
        !`${c.childFirstName} ${c.childLastName} ${c.parentName ?? ''}`
          .toLowerCase()
          .includes(q)
      )
        return false;
      const age = getAgeNum(c.dateOfBirth ?? null);
      if (age !== null && (age < minAge || (maxAge < AGE_MAX && age > maxAge)))
        return false;
      return true;
    });
  }, [checkins, search, ageRange]);

  const handleCheckOut = async () => {
    if (!checkOutTarget) return;
    setCheckingOut(true);
    try {
      await checkOutChild(checkOutTarget.id);
      setCheckOutTarget(null);
    } finally {
      setCheckingOut(false);
    }
  };

  if (authLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!userData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Typography variant="h6" textAlign="center" gutterBottom>
          Admin Sign In
        </Typography>
        <LoginPage onUserSet={Function.prototype as () => void} />
      </Container>
    );
  }

  if (!isChildcareAdmin) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Typography variant="h6" color="error" textAlign="center">
          Access Denied
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mt: 1 }}
        >
          You need the childcare role to view this page.
        </Typography>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        overflow: 'hidden',
        backgroundColor: '#eee',
      }}
    >
      {/* ── Pinned top area ── */}
      <Box sx={{ flexShrink: 0, bgcolor: 'background.default' }}>
        <Container maxWidth="lg" sx={{ pt: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Box>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h5" fontWeight="bold">
                  Admin Check-In
                </Typography>
                <Refresh
                  onClick={() => window.location.reload()}
                  sx={{
                    fontSize: 24,
                    color: 'text.secondary',
                    cursor: 'pointer',
                    ml: 1,
                  }}
                />
              </div>
              {/* <Typography variant="body2" color="text.secondary">
                Live view of all checked-in children
              </Typography> */}
            </Box>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setWalkInOpen(true)}
                sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
              >
                Walk-In Check-In
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/checkin/history')}
                sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
              >
                View History
              </Button>
              <Chip
                label={
                  loading
                    ? 'Loading…'
                    : `${filteredCheckins.length} / ${checkins.length} checked in`
                }
                sx={{ fontSize: 14, px: 1, height: 36, borderRadius: 3 }}
              />
              <IconButton
                onClick={(e) => setNotifAnchor(e.currentTarget)}
                color={unreadConversations.length > 0 ? 'primary' : 'default'}
              >
                <Badge badgeContent={unreadConversations.length} color="error">
                  <NotificationsNone />
                </Badge>
              </IconButton>
            </Stack>
          </Stack>

          {!loading && checkins.length > 0 && (
            <Stack
              direction="row"
              alignItems="stretch"
              spacing={2}
              sx={{
                mb: 2,
                // background: 'blue',
                justifyContent: 'space-between',
              }}
            >
              <TextField
                size="small"
                placeholder="Search…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearch('')}
                        edge="end"
                      >
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ width: 220 }}
              />
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ ml: 'auto' }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  whiteSpace="nowrap"
                >
                  Age: {ageRange[0]} –{' '}
                  {ageRange[1] >= AGE_MAX ? `${AGE_MAX}+` : ageRange[1]}
                </Typography>
                <Slider
                  value={ageRange}
                  onChange={(_, val) => setAgeRange(val as [number, number])}
                  min={AGE_MIN}
                  max={AGE_MAX}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(v) =>
                    v >= AGE_MAX ? `${AGE_MAX}+` : String(v)
                  }
                  size="small"
                  sx={{ width: 200 }}
                />
              </Stack>
            </Stack>
          )}
        </Container>
      </Box>

      {/* ── Scrollable grid area ── */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        <Container maxWidth="lg" sx={{ pb: 6 }}>
          {loading && (
            <Stack alignItems="center" sx={{ mt: 8 }}>
              <CircularProgress />
            </Stack>
          )}

          {!loading && checkins.length === 0 && (
            <Stack alignItems="center" spacing={2} sx={{ mt: 10 }}>
              <Typography fontSize={48}>👶</Typography>
              <Typography variant="h6" color="text.secondary">
                No children are currently checked in
              </Typography>
            </Stack>
          )}

          {!loading && checkins.length > 0 && filteredCheckins.length === 0 && (
            <Stack alignItems="center" spacing={2} sx={{ mt: 10 }}>
              <Typography variant="h6" color="text.secondary">
                No results for &ldquo;{search}&rdquo;
              </Typography>
            </Stack>
          )}

          {!loading && filteredCheckins.length > 0 && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              {filteredCheckins.map((checkin) => (
                <Grid
                  item
                  xs={12}
                  sm={4}
                  md={3}
                  lg={3}
                  key={checkin.id}
                  sx={{ display: 'flex' }}
                >
                  <CheckInCard
                    checkin={checkin}
                    onClick={() => setDetailTarget(checkin)}
                    onPrint={() => printLabels([checkin], 'admin')}
                    onCheckOut={() => setCheckOutTarget(checkin)}
                    onMessage={() =>
                      setMsgTarget({
                        childId: checkin.childId,
                        familyId: checkin.familyId,
                        childName: `${checkin.childFirstName} ${checkin.childLastName}`,
                      })
                    }
                    unreadCount={unreadByChild.get(checkin.childId) ?? 0}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>

      {/* ── Dialogs ── */}
      <CheckInDetailDialog
        checkin={detailTarget}
        onClose={() => setDetailTarget(null)}
        onCheckOut={(c) => {
          setDetailTarget(null);
          setCheckOutTarget(c);
        }}
        onPrint={(c) => printLabels([c], 'admin')}
        onMessage={(c) => {
          setDetailTarget(null);
          setMsgTarget({
            childId: c.childId,
            familyId: c.familyId,
            childName: `${c.childFirstName} ${c.childLastName}`,
          });
        }}
        unreadCount={unreadByChild.get(detailTarget?.childId ?? '') ?? 0}
      />

      {/* Notification bell popover */}
      <Popover
        open={!!notifAnchor}
        anchorEl={notifAnchor}
        onClose={() => setNotifAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 300, maxHeight: 420 } }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Messages
          </Typography>
        </Box>
        {unreadConversations.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ px: 2, pb: 2 }}
          >
            No unread messages
          </Typography>
        ) : (
          <List disablePadding>
            {unreadConversations.map((convo) => (
              <ListItemButton
                key={convo.childId}
                onClick={() => {
                  setNotifAnchor(null);
                  setMsgTarget(convo);
                }}
              >
                <ListItemAvatar>
                  <Badge
                    badgeContent={unreadByChild.get(convo.childId) ?? 0}
                    color="error"
                  >
                    <Avatar
                      sx={{
                        bgcolor: 'primary.main',
                        width: 36,
                        height: 36,
                        fontSize: 16,
                      }}
                    >
                      {convo.childName[0]}
                    </Avatar>
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  primary={convo.childName}
                  secondary={`${
                    unreadByChild.get(convo.childId) ?? 0
                  } unread message${
                    (unreadByChild.get(convo.childId) ?? 0) === 1 ? '' : 's'
                  }`}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>

      {msgTarget && (
        <ChatWindow
          open={!!msgTarget}
          onClose={() => setMsgTarget(null)}
          childId={msgTarget.childId}
          familyId={msgTarget.familyId}
          childName={msgTarget.childName}
          role="admin"
          senderId={userData?.userId ?? ''}
          senderName={`${userData?.firstName ?? ''} ${
            userData?.lastName ?? ''
          }`}
        />
      )}

      {/* Check-out confirmation */}
      <Dialog
        open={!!checkOutTarget}
        onClose={() => !checkingOut && setCheckOutTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Check Out Child</DialogTitle>
        <DialogContent>
          <Stack alignItems="center" spacing={2} sx={{ pt: 1 }}>
            <Avatar
              src={checkOutTarget?.photoUrl ?? undefined}
              sx={{
                width: 96,
                height: 96,
                fontSize: 36,
                bgcolor: 'primary.main',
              }}
            >
              {checkOutTarget?.childFirstName?.[0]}
            </Avatar>
            <DialogContentText textAlign="center">
              Check out{' '}
              <strong>
                {checkOutTarget?.childFirstName} {checkOutTarget?.childLastName}
              </strong>
              ?
            </DialogContentText>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCheckOutTarget(null)}
            disabled={checkingOut}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCheckOut}
            disabled={checkingOut}
            startIcon={checkingOut ? <CircularProgress size={16} /> : undefined}
          >
            {checkingOut ? 'Checking Out…' : 'Check Out'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Walk-in check-in */}
      <WalkInDialog
        open={walkInOpen}
        onClose={() => setWalkInOpen(false)}
        adminId={userData.userId}
      />
    </Box>
  );
};

export default CheckInAdmin;
